import { getCurrentToken } from '@infinitro/global';
import { supportChatDebug, supportChatWarn } from '../debug';
import { supportChatWsUrl } from './http';
/**
 * Parses the server envelope `{ "type": "...", "data": { ... } }`.
 * Falls back to `event` key and flat layout for forward-compat.
 */
function parseIncomingEvent(raw) {
    if (raw === null || typeof raw !== 'object')
        return null;
    const o = raw;
    const name = (typeof o.type === 'string' ? o.type : typeof o.event === 'string' ? o.event : null);
    if (!name)
        return null;
    // Payload lives in `data`; fall back to the root object for flat layouts.
    const data = o.data !== undefined && o.data !== null && typeof o.data === 'object'
        ? o.data
        : o;
    return { name, data };
}
/**
 * Opens `GET /ws?token=<jwt>` (WebSocket). Call disconnect when the shell unmounts or user logs out.
 */
export function connectSupportChatSocket(handlers) {
    const token = getCurrentToken();
    if (!token) {
        supportChatWarn('WebSocket skipped: missing auth token');
        handlers.onConnectionChange?.(false);
        return () => { };
    }
    let ws;
    const url = supportChatWsUrl(token);
    try {
        supportChatDebug('WebSocket connecting', { url });
        ws = new WebSocket(url);
    }
    catch (e) {
        supportChatWarn('WebSocket construct failed', { error: e, url });
        handlers.onConnectionChange?.(false);
        return () => { };
    }
    ws.onopen = () => {
        supportChatDebug('WebSocket open');
        handlers.onConnectionChange?.(true);
    };
    ws.onclose = (event) => {
        supportChatWarn('WebSocket closed', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
        });
        handlers.onConnectionChange?.(false);
    };
    ws.onerror = () => {
        supportChatWarn('WebSocket error event');
        handlers.onConnectionChange?.(false);
    };
    ws.onmessage = (ev) => {
        try {
            const raw = typeof ev.data === 'string' ? JSON.parse(ev.data) : ev.data;
            const parsed = parseIncomingEvent(raw);
            if (!parsed)
                return;
            const { name, data } = parsed;
            const conversationId = typeof data.conversation_id === 'string' ? data.conversation_id : undefined;
            supportChatDebug('WebSocket message received', { event: name, conversationId });
            switch (name) {
                // { "type": "message", "data": { id, conversation_id, sender_type, content, ... } }
                // data IS the SupportMessage; backend also echoes the customer's own message.
                case 'message': {
                    if (conversationId) {
                        handlers.onMessage?.({
                            conversation_id: conversationId,
                            message: data
                        });
                    }
                    else {
                        supportChatWarn('WebSocket "message" event missing conversation_id', { data });
                    }
                    break;
                }
                // { "type": "handoff", "data": { conversation_id, from_agent_id, reason } }
                case 'handoff': {
                    if (conversationId) {
                        handlers.onHandoff?.({
                            conversation_id: conversationId,
                            payload: data
                        });
                    }
                    else {
                        supportChatWarn('WebSocket "handoff" event missing conversation_id', { data });
                    }
                    break;
                }
                // { "type": "server_shutdown", "data": { "message": "..." } }
                case 'server_shutdown': {
                    const reason = typeof data.message === 'string' ? data.message : undefined;
                    handlers.onServerShutdown?.(reason);
                    ws.close();
                    break;
                }
                default:
                    supportChatDebug('WebSocket message ignored', { event: name });
                    break;
            }
        }
        catch (e) {
            supportChatWarn('WebSocket message parse failed', { error: e });
        }
    };
    return () => {
        try {
            supportChatDebug('WebSocket disconnect requested');
            ws.close();
        }
        catch {
            /* noop */
        }
    };
}
